/**
 * Camada 1 · a conversa, a tela do canal que o sino anuncia.
 *
 * O SINO E A CONVERSA COMPARTILHAM O MESMO TRANSPORTE, e o cabecalho de `index.ts` diz o
 * porque: dois canais separados divergem, e o sintoma disso e o contador marcando mensagem
 * que ja foi lida. Esta tela marca como lida o que ela DESENHOU, e e essa marcacao que
 * derruba o numero do sino.
 *
 * TODA SAIDA DE IA LEVA A MARCA DE QUE E SUGESTAO. O contrato carrega `sugestaoDeIA` para
 * a marca nao depender de quem escreve cada tela lembrar dela, e este componente e quem
 * cumpre a promessa: mensagem com a marca sai visualmente distinta E anunciada como tal
 * para quem usa leitor de tela.
 *
 * O `tenantId` VIAJA EXPLICITO em toda chamada. E a segunda tranca do isolamento, e nao
 * redundancia com o filtro do banco: isolamento aqui e incidente, e nao bug.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { EstadoErro, EstadoVazio, IconeIa } from '@complexity/ui';
import type { Mensagem, TransporteChat } from '../index.js';

export interface PainelConversaProps {
  readonly tenantId: string;
  readonly conversaId: string;
  readonly transporte: TransporteChat;
  /** Quem esta olhando. Decide o lado da bolha e o que conta como nao lida. */
  readonly eu: 'cliente' | 'operacao';
}

export function PainelConversa({ tenantId, conversaId, transporte, eu }: PainelConversaProps) {
  const [mensagens, definirMensagens] = useState<readonly Mensagem[]>([]);
  const [carregando, definirCarregando] = useState(true);
  const [falha, definirFalha] = useState<string | null>(null);
  const [rascunho, definirRascunho] = useState('');
  const [enviando, definirEnviando] = useState(false);

  const fimRef = useRef<HTMLDivElement>(null);

  // A carga inicial. A falha vira estado PROPRIO, e nao lista vazia: os dois se parecem na
  // tela e sao opostos, e confundi-los faz a pessoa concluir que ninguem escreveu quando o
  // que houve foi uma consulta que nao respondeu.
  useEffect(() => {
    let vivo = true;
    definirCarregando(true);
    definirFalha(null);

    transporte
      .listarMensagens(tenantId, conversaId)
      .then((lista) => {
        if (!vivo) return;
        definirMensagens(lista);
        definirCarregando(false);
      })
      .catch(() => {
        if (!vivo) return;
        definirFalha('A conversa não respondeu.');
        definirCarregando(false);
      });

    return () => {
      vivo = false;
    };
  }, [tenantId, conversaId, transporte]);

  // O fluxo ao vivo. O RETORNO CANCELA A ASSINATURA, e o contrato exige isso com todas as
  // letras: sem o cancelamento, cada abertura de tela deixa uma conexao aberta, e depois de
  // algumas navegacoes a mesma mensagem chega varias vezes.
  useEffect(() => {
    const cancelar = transporte.assinar(tenantId, (nova) => {
      // A mensagem chega do canal do TENANT, e nao da conversa. Filtrar aqui evita que
      // mensagem de outra conversa do mesmo cliente apareca nesta tela.
      if (nova.conversaId !== conversaId) return;
      definirMensagens((antes) =>
        // O canal pode reenviar o que ja chegou, e sem esta guarda a mensagem duplica na
        // tela. Comparar por id e barato e resolve.
        antes.some((m) => m.id === nova.id) ? antes : [...antes, nova],
      );
    });
    return cancelar;
  }, [tenantId, conversaId, transporte]);

  // Rola para o fim quando chega mensagem. `block: 'nearest'` para nao arrastar a pagina
  // inteira quando a conversa esta dentro de um painel.
  useEffect(() => {
    fimRef.current?.scrollIntoView({ block: 'nearest' });
  }, [mensagens.length]);

  // Marca como lida o que esta na tela e nao e meu. E esta marcacao que derruba o contador
  // do sino, e por isso ela mora aqui e nao no sino: o sino nao sabe o que foi visto.
  useEffect(() => {
    for (const m of mensagens) {
      if (m.lidaEm === null && m.autor !== eu) {
        void transporte.marcarLida(tenantId, m.id).catch(() => {
          // A falha em marcar NAO derruba a tela nem vira aviso: o pior efeito e o contador
          // continuar alto por mais um ciclo, e um erro na cara da pessoa por isso custaria
          // mais que o proprio defeito.
        });
      }
    }
  }, [mensagens, eu, tenantId, transporte]);

  const enviar = useCallback(async () => {
    const texto = rascunho.trim();
    if (texto === '' || enviando) return;

    definirEnviando(true);
    try {
      const nova = await transporte.enviar(tenantId, conversaId, texto);
      definirMensagens((antes) => (antes.some((m) => m.id === nova.id) ? antes : [...antes, nova]));
      // O rascunho so e limpo DEPOIS do sucesso. Limpar antes perderia o texto da pessoa
      // quando a rede falha, e reescrever o que ja foi escrito e o pior tipo de retrabalho.
      definirRascunho('');
    } catch {
      definirFalha('Não conseguimos enviar. Seu texto continua aqui.');
    } finally {
      definirEnviando(false);
    }
  }, [rascunho, enviando, transporte, tenantId, conversaId]);

  if (carregando) {
    return <p className="p-cx-4 text-cx-sm text-texto-suave">Carregando a conversa…</p>;
  }

  if (falha !== null && mensagens.length === 0) {
    return (
      <div className="p-cx-4">
        <EstadoErro titulo="A conversa não abriu" mensagem={falha}>
          Nada foi perdido. Feche e abra de novo em instantes.
        </EstadoErro>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-cx-4" aria-live="polite">
        {mensagens.length === 0 ? (
          <EstadoVazio titulo="Nenhuma mensagem ainda">
            Escreva abaixo e a nossa equipe responde por aqui.
          </EstadoVazio>
        ) : (
          <ul className="flex flex-col gap-cx-3">
            {mensagens.map((m) => {
              const meu = m.autor === eu;
              return (
                <li
                  key={m.id}
                  className={`flex flex-col gap-cx-1 ${meu ? 'items-end' : 'items-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-cx-lg p-cx-3 text-cx-sm ${
                      meu
                        ? 'bg-marca text-superficie'
                        : 'border border-borda bg-superficie text-texto'
                    }`}
                  >
                    {/* A MARCA DE IA, e ela e obrigatoria. Toda saida de modelo na tela diz
                        que e sugestao, e o texto vai no `sr-only` para quem ouve receber a
                        mesma informacao que quem ve, e nao so um icone mudo. */}
                    {m.sugestaoDeIA && (
                      <span className="mb-cx-1 flex items-center gap-cx-1 text-cx-xs font-medium opacity-90">
                        <IconeIa />
                        <span>Sugestão de IA</span>
                      </span>
                    )}
                    <span className="whitespace-pre-wrap">{m.texto}</span>
                  </div>
                  <time
                    dateTime={m.enviadaEm.toISOString()}
                    className="text-cx-xs text-texto-suave"
                  >
                    {m.enviadaEm.toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </time>
                </li>
              );
            })}
          </ul>
        )}
        <div ref={fimRef} />
      </div>

      {/* A falha de envio aparece SEM apagar a lista: a conversa continua legivel, e o
          rascunho continua no campo. */}
      {falha !== null && mensagens.length > 0 && (
        <p role="alert" className="px-cx-4 pb-cx-2 text-cx-xs text-erro">
          {falha}
        </p>
      )}

      <form
        className="flex items-end gap-cx-2 border-t border-borda p-cx-3"
        onSubmit={(e) => {
          e.preventDefault();
          void enviar();
        }}
      >
        <textarea
          value={rascunho}
          onChange={(e) => definirRascunho(e.target.value)}
          onKeyDown={(e) => {
            // Enter envia, Shift+Enter quebra linha. E a convencao que a pessoa ja traz de
            // todo outro app de mensagem, e contrariar isso faz ela enviar sem querer.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void enviar();
            }
          }}
          rows={2}
          placeholder="Escreva a sua mensagem"
          aria-label="Sua mensagem"
          className="min-h-[44px] flex-1 resize-none rounded-cx-md border border-borda bg-superficie p-cx-2 text-cx-sm text-texto"
        />
        <button
          type="submit"
          disabled={rascunho.trim() === '' || enviando}
          className="min-h-[44px] min-w-[44px] rounded-cx-md bg-marca px-cx-4 text-cx-sm font-medium text-superficie disabled:opacity-50"
        >
          {enviando ? 'Enviando…' : 'Enviar'}
        </button>
      </form>
    </div>
  );
}
